import ButtonT from '@components/tailwind-components/Button/ButtonT';
import InputWithLabelT from '@components/tailwind-components/Input/InputWithLabelT';
import SelectInputWithLabelT from '@components/tailwind-components/Input/SelectInputWithLabelT';
import {
  operatorList,
  type TDecideRule,
  TDecideRuleSchema,
} from '@conversed/shared/src/central/types/blocks/Decider';
import { zodResolver } from '@hookform/resolvers/zod';
import { useBlocksUpdates } from '@hooks/flow/block/useBlocksUpdates';
import { useFlow } from '@hooks/flow/flow/useFlow';
import { useModal } from '@hooks/modal/useModal';
import { t } from 'i18next';
import type React from 'react';
import { useCallback } from 'react';
import { useForm } from 'react-hook-form';

export const formSchema = TDecideRuleSchema.superRefine((data, ctx) => {
  if (!data.operand?.trim()) {
    return ctx.addIssue({
      path: ['operand'],
      message: t('general.validation.required'),
      code: 'custom',
    });
  }

  switch (data.operator) {
    case 'gt':
    case 'lt':
    case 'ge':
    case 'le':
      if (Number.isNaN(Number(data.operand))) {
        ctx.addIssue({
          path: ['operand'],
          message: t('flow.validation.operand.number'),
          code: 'custom',
        });
      }
      break;
    case 'exists':
      if (!['true', 'false'].includes(data.operand.toLowerCase())) {
        ctx.addIssue({
          path: ['operand'],
          message: t('flow.validation.operand.boolean'),
          code: 'custom',
        });
      }
      break;
  }
});

export const EditRulesForDestinationModal: React.FC = () => {
  const { selectedBlock, targetBlock } = useFlow();
  const { updateBlocks } = useBlocksUpdates();

  const { hideModal } = useModal();

  const { handleSubmit, control } = useForm({
    resolver: zodResolver(formSchema),
    shouldUseNativeValidation: true,
    defaultValues: { operand: '', operator: 'eq' as const },
  });

  // The operand value should be converted to the correct type based on the operator, this operand value is always coming as a string from the form.
  const setOperandByOperator = useCallback(
    (operand: string, operator: TDecideRule['operator']) => {
      switch (operator) {
        case 'gt':
        case 'lt':
        case 'ge':
        case 'le':
          return Number(operand);
        case 'exists':
          return operand?.toLowerCase() === 'true';
        default:
          // includes, startsWith, endsWith - only can be string
          // eq, ne - can be any type
          return operand;
      }
    },
    [],
  );

  const onSubmit = useCallback(
    (data: TDecideRule) => {
      if (
        !selectedBlock ||
        !targetBlock?.id ||
        selectedBlock.type !== 'Decider'
      ) {
        return;
      }

      const updatedBlock = {
        ...selectedBlock,
        params: {
          ...selectedBlock.params,
          possibleDestinations: [
            ...selectedBlock.params.possibleDestinations,
            {
              blockId: targetBlock.id,
              rules: [
                {
                  operand: setOperandByOperator(data.operand, data.operator),
                  operator: data.operator,
                },
              ],
            },
          ],
        },
      };

      updateBlocks([updatedBlock]);
      return hideModal();
    },
    [
      hideModal,
      selectedBlock,
      targetBlock?.id,
      updateBlocks,
      setOperandByOperator,
    ],
  );
  return (
    <form className='tailwind-modal-form' onSubmit={handleSubmit(onSubmit)}>
      <div className='tailwind-modal-body'>
        <p className='text-sm mb-2 italic'>
          {t('flow.form.description.rule', { route: targetBlock?.id })}
        </p>
        <div className='grid grid-cols-1 gap-x-6 gap-y-2'>
          <div className='col-span-1'>
            <InputWithLabelT
              type='text'
              field='operand'
              placeholder={t('flow.form.description.operand')}
              label={t('flow.form.description.operand')}
              id='operand'
              control={control}
              options={{
                required: t('general.validation.required'),
              }}
            />

            <SelectInputWithLabelT
              field='operator'
              label={t('flow.form.description.operator')}
              control={control}
              values={operatorList.map((value) => ({
                value: value,
                description: value,
              }))}
            />
          </div>
        </div>

        <div className='tailwind-modal-footer'>
          <ButtonT type='submit' styling='primary' size='xs' rounding='md'>
            {t('general.buttons.save')}
          </ButtonT>
        </div>
      </div>
    </form>
  );
};
